import { PageHeader } from '@/components/patterns';
import { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/lib/hooks/use-toast';
import { storeConnectionsApi } from '../services';
import { ConnectStoreDialog } from '../components/connect-store-dialog';
import type { StoreConnection } from '../types';
import { Plus, Store, Wifi, Pencil, Loader2, Unplug } from 'lucide-react';
import {
  AVISO_SOLO_MIRA,
  atributosSoloMira,
  useSoporteSoloMira,
} from '@/features/mini-prixia/soporte-solo-mira';

export function StoreConnectionsPage() {
  const { toast } = useToast();
  // Demo con «Entrar como»: conectar, editar, probar o desconectar escriben (403 en el servidor).
  const soloMira = useSoporteSoloMira();
  const [connections, setConnections] = useState<StoreConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<StoreConnection | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StoreConnection | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    try {
      const data = await storeConnectionsApi.getAll();
      setConnections(data);
    } catch {
      toast({ title: 'Error al cargar conexiones', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleTest = async (conn: StoreConnection) => {
    setTestingId(conn.id);
    try {
      const result = await storeConnectionsApi.test(conn.id);
      const isOk = result?.status === 'ok';
      toast({
        title: isOk ? '✅ Conexión exitosa' : 'Error de conexión',
        description: isOk
          ? `La tienda "${conn.name}" responde correctamente`
          : 'No se pudo conectar',
        variant: isOk ? 'default' : 'destructive',
      });
    } catch (err: any) {
      toast({
        title: 'Error al probar conexión',
        description: err?.message,
        variant: 'destructive',
      });
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await storeConnectionsApi.delete(deleteTarget.id);
      toast({ title: 'Conexión eliminada' });
      setDeleteTarget(null);
      fetchConnections();
    } catch (err: any) {
      toast({ title: 'Error al eliminar', description: err?.message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  /** «Publicar al aprobar»: se guarda al momento; si falla, vuelve como estaba. */
  const cambiarPublicacion = async (conn: StoreConnection, autoPublish: boolean) => {
    setConnections((cs) => cs.map((c) => (c.id === conn.id ? { ...c, autoPublish } : c)));
    try {
      await storeConnectionsApi.update(conn.id, { autoPublish });
      toast({
        title: autoPublish ? 'Publicar al aprobar: encendido' : 'Publicar al aprobar: apagado',
        description: autoPublish
          ? `Lo que apruebes en Decisiones llega solo a ${conn.name}.`
          : `Los precios aprobados se mandan a ${conn.name} con «Aplicar a tienda».`,
      });
    } catch (err: any) {
      setConnections((cs) =>
        cs.map((c) => (c.id === conn.id ? { ...c, autoPublish: !autoPublish } : c))
      );
      toast({ title: 'No se pudo guardar', description: err?.message, variant: 'destructive' });
    }
  };

  const openEdit = (conn: StoreConnection) => {
    setEditTarget(conn);
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditTarget(null);
    setDialogOpen(true);
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        <PageHeader
          etiqueta="Integraciones"
          tonoEtiqueta="neutro"
          titulo="Tiendas conectadas"
          subtitulo="Las tiendas online a las que Prixia les manda los precios: solos al aprobar en Decisiones (si lo enciendes abajo) o con «Aplicar a tienda» en Compite › Productos."
          acciones={
            <Button
              variant="degradado"
              size="lg"
              className="px-5"
              onClick={openCreate}
              {...atributosSoloMira(soloMira)}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Conectar tienda
            </Button>
          }
        />

        {/* Content */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : connections.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Store className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">
                  No hay tiendas conectadas
                </p>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                  Conecta tu tienda Shopify, WooCommerce u otra plataforma para enviar precios
                  calculados automáticamente.
                </p>
                <Button className="mt-4" onClick={openCreate} {...atributosSoloMira(soloMira)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Conectar primera tienda
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Publicar al aprobar</TableHead>
                    <TableHead>Último push</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {connections.map((conn) => (
                    <TableRow key={conn.id}>
                      <TableCell className="font-medium">{conn.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                        {conn.storeUrl}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            conn.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                          }
                        >
                          {conn.isActive ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!conn.autoPublish}
                          onCheckedChange={(v) => cambiarPublicacion(conn, v)}
                          {...atributosSoloMira(soloMira)}
                          aria-label={`Publicar en ${conn.name} lo que se aprueba en Decisiones`}
                        />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {conn.lastPushAt
                          ? new Date(conn.lastPushAt).toLocaleString('es-MX', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'Nunca'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleTest(conn)}
                            disabled={testingId === conn.id || soloMira}
                            title={soloMira ? AVISO_SOLO_MIRA : 'Probar conexión'}
                          >
                            {testingId === conn.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Wifi className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEdit(conn)}
                            disabled={soloMira}
                            title={soloMira ? AVISO_SOLO_MIRA : 'Editar'}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(conn)}
                            disabled={soloMira}
                            title={soloMira ? AVISO_SOLO_MIRA : 'Desconectar'}
                          >
                            <Unplug className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog crear/editar */}
      <ConnectStoreDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editConnection={editTarget}
        onSaved={fetchConnections}
      />

      {/* Dialog confirmar eliminación */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Desconectar tienda</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de desconectar <strong>{deleteTarget?.name}</strong>? Ya no podrás
              enviar precios a esta tienda.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Desconectar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default StoreConnectionsPage;
